/**
 * GirisKartiSekmesi.jsx — denetmen kişi sayfasının (PersonelDetayPage) "Giriş Kartı" sekmesi.
 * Backlog #367 (Detaylar sekmesi kalktı, "Giriş Kartı Durumu" kendi sekmesine taşındı) + #365 (firma anahtarı yok).
 *
 * Kart kararı tek bir blok (#400; #367'de firma başına bir bloktu): üst çubuktaki fuarda kişinin görevli olduğu firmaların
 * kararları kartBirlestir.js ile TEK karara iner — Kart Yazdır penceresinin gövdesiyle aynı içerik: karar rozeti, kartı
 * engelleyen maddeler, 24 saat ve KKD / SGK uyarıları. Engeller firmaya bağlanmaz ("SGK: Eksik" bir kez yazılır, birden çok
 * firmalı kişide her firma için tekrarlanmaz); firma evrakı yalnızca HİÇBİR firmada tam değilse uyarılır ve "Firma Profili"
 * bağlantısı hangi firmaya gidileceğini sorar. Veri sayfanın `GET /personnel/{id}/detay` okumasındaki `firmalar[]` —
 * kart-karari girdileriyle aynı, yani liste satırı, Kart Yazdır penceresi ve bu sekme birbirinden ayrışmaz. Kural backend'de,
 * burası yalnızca gösterir.
 *
 * #480: engellerin altında "Tamam olanlar" — zaten düzende olan maddeler yeşil satırlar olarak. Her satır bir OLGU'dur (onaylı
 * belge, doğrulanmış kimlik, tamamlanmış firma grubu...), yeniden türetilmiş bir karar değil: rozet backend'in kalır (2026-08-28'deki
 * yanlış "Kart Alabilir" kuralın tarayıcıda türetilmesinden gelmişti). Yalnızca bu sekme; Kart Yazdır penceresi aynen kalır.
 *
 * Fuar yoksa (üst çubukta seçili fuar yok) karar hesaplanmaz: tek satırlık bilgi gösterilir.
 *
 * @param {object}   person      Kişi Listesi satırı (`detay.kisi`)
 * @param {object}   detay       GET /detay yanıtı (`fuardaGorevli`, `firmalar[]`)
 * @param {number}   fuarId      üst çubuktaki fuar; yoksa null
 * @param {string}   fuarAdi     o fuarın adı
 * @param {Function} onSayfaIci  ({ tab, alt, belge, vurgu }) — kişi sayfasının kendi sekmesine geç ve hedefi çerçevele (KartUygunsuzluklari)
 * @param {Function} onKartYazdir () => void — Kart Yazdır penceresini aç (sayfa yönetir)
 */
import React from "react";
import { Alert, Button, Card, CardBody, CardHeader } from "reactstrap";
import { Link } from "react-router-dom";
import { Building2, IdCard, Printer, Receipt, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import KartUygunsuzluklari, { IkonKaresi, kartUyarilari, uygunsuzlukLabel } from "../KartUygunsuzluklari";
import { firmaAdi } from "../PersonelFirmaBelgeParcalari";
import { PERSONEL_BELGE_META } from "../PersonelKartParcalari";
import { statusBackground, STATUS_HEX } from "../../Projects/StandPills";
import { kartKararMeta, isverenYolu, ISVEREN_ETIKETI } from "../../../services/kartDurumuService";
import { cardAccessModeMeta } from "../../../services/cardAccessService";
import { kisiBelgeLabel } from "../../../services/kisiAdminService";
import { kartKararBirlestir } from "../kartBirlestir";

/** Onaylı, yaşı/süresi geçmemiş bir belge mi (satırdaki `documents[key]`). */
const belgeTamam = (doc) => doc?.status === 1 && !doc.yasiGecmis && !doc.expired;

/**
 * #480: kartın zaten düzende olan maddeleri — `[{ key, Icon, metin }]`. Yalnızca satırdaki olgulardan: istenen belgelerden onaylı olanlar
 * (`gerekliBelgeler`), KVKK (belge · durum 3 · dijital onay), doğrulanmış kimlik, tamamlanmış firma grupları, firmanın vergi numarası.
 * Hiçbir şey "istenmiyor" diye listelenmez (Özel İzin / İşveren yoluyla geçilen belgeler bu listede yok).
 */
function saglananlar(girdi, t) {
  const kisi = girdi?.kisi;
  if (!kisi) return [];
  const satirlar = [];
  const docs = kisi.documents || {};

  const kvkkTamam = belgeTamam(docs.kvkk) || kisi.kvkkDijitalOnay?.tamam === true || kisi.personKvkkStatusId === 3;
  if (kvkkTamam) satirlar.push({ key: "kvkk", Icon: ShieldCheck, metin: t("girisKartiSekmesi.kvkkTamam") });

  if (Number(kisi.kimlikDogrulama?.durum) === 1) {
    satirlar.push({ key: "kimlik", Icon: IdCard, metin: t("girisKartiSekmesi.kimlikDogrulandi") });
  }

  (kisi.gerekliBelgeler || []).forEach((key) => {
    if (key === "kvkk" || !belgeTamam(docs[key])) return;
    const Icon = PERSONEL_BELGE_META.find((m) => m.key === key)?.icon ?? ShieldCheck;
    satirlar.push({ key: `belge-${key}`, Icon, metin: t("girisKartiSekmesi.belgeOnaylandi", { belge: kisiBelgeLabel(key) }) });
  });

  (girdi.belgeGruplari || kisi.firmaBelgeGruplari || []).forEach((g) => {
    if (g.gerekli === false || !g.tamam) return;
    satirlar.push({ key: `grup-${g.kod}`, Icon: Building2, metin: t("girisKartiSekmesi.firmaGrubuTamam", { grup: g.ad }) });
  });

  if (girdi.kartErisim?.taxNumberSet === true && girdi.kartErisim?.taxNumberGerekli !== false) {
    satirlar.push({ key: "vergi", Icon: Receipt, metin: t("girisKartiSekmesi.vergiNoVar") });
  }
  return satirlar;
}

/** Kişinin fuardaki firma kararlarından türeyen tek blok — Kart Yazdır penceresinin gövdesiyle aynı. */
function KararBlogu({ firmalar, tercihFirmaId, personId, fuarId, onSayfaIci }) {
  const { t } = useTranslation("components");
  const birlesik = kartKararBirlestir(firmalar, { tercihFirmaId });
  if (!birlesik) return null;
  const girdi = birlesik.secili;
  const karar = kartKararMeta(girdi);
  const gecici24 = girdi.kartErisim?.cardAccessMode === "TEMPORARY_24H";
  const uyarilar = kartUyarilari(girdi.kisi);
  const firmaGrubu = (girdi.belgeGruplari || girdi.kisi?.firmaBelgeGruplari || []).find((g) => g.kod === "FIRMA") ?? null;
  const tamamlar = saglananlar(girdi, t);

  return (
    <Card className="mb-3">
      <CardHeader className="bg-transparent d-flex align-items-center gap-2 flex-wrap">
        <span className="badge text-white fw-normal" style={{ background: statusBackground(karar.color) }}>{karar.label}</span>
        <span className="text-muted small">
          {firmaAdi(girdi)}{birlesik.digerFirmaSayisi > 0 && ` +${birlesik.digerFirmaSayisi}`}
        </span>
        {isverenYolu(girdi) && (
          girdi.isverenFirmaId ? (
            <Link to={`/Management/Firmalar/Profil/${girdi.isverenFirmaId}?tab=belgeler&fuarId=${fuarId}`} className="small">
              {ISVEREN_ETIKETI} ({girdi.isverenFirmaAdi || `Firma #${girdi.isverenFirmaId}`})
            </Link>
          ) : <span className="text-muted small">{ISVEREN_ETIKETI}</span>
        )}
      </CardHeader>
      <CardBody>
        <KartUygunsuzluklari
          uygunsuzluklar={birlesik.uygunsuzluklar} vergiNoYok={birlesik.vergiNoYok} firmaGrubu={firmaGrubu}
          firmaSecenekleri={birlesik.firmaSecenekleri}
          firmaId={girdi.firmaId} personId={personId} fuarId={fuarId} onSayfaIci={onSayfaIci} kisi={girdi.kisi}
        />
        {tamamlar.length > 0 && (
          <div className="mt-3 pt-3 border-top">
            <div className="small text-muted mb-2">{t("girisKartiSekmesi.tamamOlanlar")}</div>
            <ul className="list-unstyled mb-0">
              {tamamlar.map(({ key, Icon, metin }) => (
                <li key={key} className="d-flex align-items-center gap-2 mb-2">
                  <IkonKaresi Icon={Icon} renk={STATUS_HEX.success} baslik={metin} />
                  <span>{metin}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {gecici24 && (
          <Alert fade={false} color="warning" className="mt-3 mb-0 py-2 small">
            <strong>{cardAccessModeMeta("TEMPORARY_24H").label}.</strong>{" "}
            {girdi.eksikFuarBelgeleri?.length > 0
              ? t("girisKartiSekmesi.eksikFirmaEvraklari", { belgeler: girdi.eksikFuarBelgeleri.map((b) => b.fileTypeName).join(", ") })
              : null}
            {" "}{t("girisKartiSekmesi.yirmiDortSaat")}
          </Alert>
        )}
        {uyarilar.map((u) => (
          <Alert key={u} fade={false} color="warning" className="mt-2 mb-0 py-2 small">{u}</Alert>
        ))}
      </CardBody>
    </Card>
  );
}

export default function GirisKartiSekmesi({ person, detay, fuarId, fuarAdi, onSayfaIci, onKartYazdir }) {
  const { t } = useTranslation("components");
  const firmalar = detay?.firmalar || [];

  return (
    <>
      <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
        <h5 className="mb-0">{t("girisKartiSekmesi.baslik")}</h5>
        {fuarId && fuarAdi && <span className="badge bg-light text-body border fw-normal">{fuarAdi}</span>}
        <Button color="primary" size="sm" className="ms-auto" disabled={!fuarId} onClick={onKartYazdir}>
          <Printer size={14} className="me-1" />{t("girisKartiSekmesi.kartYazdir")}
        </Button>
      </div>

      {!fuarId && <div className="text-muted small">{t("girisKartiSekmesi.onceFuarSecin")}</div>}

      {fuarId && detay && !detay.fuardaGorevli && (
        <Alert fade={false} color="light" className="border mb-0">
          {t("girisKartiSekmesi.gorevlendirilmemis")}
          {person?.forbidden && <div className="mt-1">{uygunsuzlukLabel("YASAKLI")}</div>}
        </Alert>
      )}

      {fuarId && detay?.fuardaGorevli && (
        <KararBlogu
          firmalar={firmalar} tercihFirmaId={detay.kartFirmaId} personId={person.personId} fuarId={fuarId}
          onSayfaIci={onSayfaIci}
        />
      )}
    </>
  );
}
